import { getRequest, postRequest } from "../http/httpRequest";
import { turboField } from "../helpers/TurboStore";
import { throwIfNotOk } from "../helpers/ApiErrorHelper";
import AiConvertor from "../converters/uiToBackend/AiConvertor";
import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import {
  AiGenerateBulkProductsRequest,
  AiGenerateBulkProductsResponse,
  TurboAvailabilityResponse,
} from "../../interfaces/AiObject";
import {
  BulkImageAnalyzeContext,
  BulkImageAnalyzeResponse,
} from "../../interfaces/ImageAnalyzeObject";
import {
  ImageRevampRequest,
  ImageRevampResponse,
  imageRevampErrorCopy,
} from "../../interfaces/ImageRevampObject";
import {
  PosterRequest,
  PosterResponse,
  posterErrorCopy,
} from "../../interfaces/PosterObject";
import {
  VisitingCardRequest,
  VisitingCardResponse,
  visitingCardErrorCopy,
} from "../../interfaces/VisitingCardObject";
import {
  NfcCardRequest,
  NfcCardResponse,
  nfcCardErrorCopy,
} from "../../interfaces/NfcCardObject";

/**
 * Sends product images to backend for AI-powered autofill analysis.
 * @param imageFormData - FormData containing image files under 'files' key
 * @param age - Product age in years
 * @returns Promise with autofill data (title, category, attributes, description, confidence)
 */
async function fetchAutofill(
  imageFormData: FormData,
  age: number
): Promise<any> {
  const endpoint = ServiceEndpoints.AI_FETCH_AUTOFILL;
  const payload = AiConvertor.buildAutofillPayload(imageFormData, age);
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  const data = await response.json();
  return data;
}

/**
 * Sends product details and image to backend for AI-powered market price suggestion.
 * @param productDetails - Product metadata object (title, category, attributes, etc.)
 * @param imageFile - Single product image file
 * @returns Promise with market price data (suggestedPrice, reasoning)
 */
async function fetchMarketPrice(
  productDetails: any,
  imageFormData: FormData | any,
): Promise<any> {
  const endpoint = ServiceEndpoints.AI_FETCH_MARKET_PRICE;
  const payload = AiConvertor.buildMarketPricePayload(imageFormData,productDetails);
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  const data = await response.json();
  return data;
}

/**
 * Bulk product generation for NineSetupBulkActions' "Generate payload" flow.
 * Sends business/category/pricing context plus the curator's four fields; the
 * backend turns that into a high-intent prompt, runs it against a model, and
 * returns one entry per requested product — see AiObject.ts for the full
 * contract and AiConvertor.mapGeneratedProductsToBulkItems for how the
 * response becomes the same `{ product: {...} }[]` shape a pasted payload has.
 *
 * ⚠️ Path unverified against a spec — the RESPONSE shape (AiObject.ts) has
 * been confirmed against a real call; the endpoint path itself and the
 * request shape are still the original proposal.
 */
async function generateBulkProducts(
  request: AiGenerateBulkProductsRequest
): Promise<AiGenerateBulkProductsResponse> {
  const endpoint = ServiceEndpoints.AI_GENERATE_BULK_PRODUCTS;
  const includeBearerToken = true;
  const body = { ...request, ...turboField(request.business?.businessId) };
  const response = await postRequest(endpoint, body, includeBearerToken);
  await throwIfNotOk(
    response,
    "Couldn't generate those products. Please try again."
  );
  return response.json();
}

/**
 * Sends product photos plus business context and gets finished product drafts
 * back — one per item the model finds — each already shaped as a CreateProductRq
 * that can be posted to bulkCreateProduct unchanged. See
 * interfaces/ImageAnalyzeObject.ts for the full contract.
 *
 * Distinct from the two calls above, which are easy to confuse with it:
 * fetchAutofill reads only the first photo and returns loose fields with
 * nothing created or stored; generateBulkProducts takes seller text and no
 * photos at all.
 *
 * Three things worth knowing at the call site:
 *
 *  - The photos are PERSISTED by a successful call. By the time this resolves
 *    the originals are in Supabase under products/drafts/{analysisId}/ and the
 *    drafts point at them, so nothing is uploaded again at the create step. An
 *    abandoned analysis leaves those objects for a TTL sweep — harmless, but a
 *    reason not to call this speculatively.
 *  - It is slow: 5–20s is normal for a vision model, and callers must tolerate
 *    the upper end rather than timing out around it.
 *  - A 200 does NOT mean everything worked. Per-image and per-product problems
 *    come back inside `errors` on an otherwise successful call and are usually
 *    present and usually fine; `error` at the top level is the only total
 *    failure, and it never accompanies a 200.
 *
 * throwIfNotOk covers the non-2xx cases — including 415, which is what a
 * `context` part without its own application/json content type produces (see
 * AiConvertor.buildBulkImageAnalyzePayload). A 415 comes back in Spring's own
 * shape rather than this API's, so the fallback copy below is what surfaces.
 */
async function bulkImageAnalyze(
  images: File[],
  context: BulkImageAnalyzeContext
): Promise<BulkImageAnalyzeResponse> {
  const endpoint = ServiceEndpoints.AI_BULK_IMAGE_ANALYZE;
  const payload = AiConvertor.buildBulkImageAnalyzePayload(images, context);
  const includeBearerToken = true;
  const response = await postRequest(endpoint, payload, includeBearerToken);
  await throwIfNotOk(
    response,
    "Couldn't analyse those photos. Please try again."
  );
  return response.json();
}

/**
 * AI photo revamp: enhance a product photo, place it in a real-world use
 * context (VISUALISE), or swap its background — see
 * interfaces/ImageRevampObject.ts for the full contract.
 *
 * Distinct from bulkImageAnalyze/fetchAutofill/generateBulkProducts above:
 * those turn photos into product drafts (title, category, price, …). This
 * takes ONE already-uploaded photo (`request.sourceImage.objectPath`) and
 * returns an edited/staged version of that same photo, inline as base64 —
 * no product fields, and nothing is stored server-side.
 *
 * Deliberately does NOT use throwIfNotOk here (unlike every other call in
 * this file): that helper's extractApiErrorMessage reads
 * `errorInformation[0].errorDetail` but drops `errorCode` on the floor, and
 * `errorCode` is exactly what IMAGE_REVAMP_ERROR_COPY needs to give a
 * curator/owner useful copy (e.g. AI_IMG_UNSUPPORTED_FORMAT → "convert it to
 * JPEG or PNG" instead of whatever terser string the server put in
 * errorDetail). 502/503/504 still get the same friendly bare-gateway copy
 * throwIfNotOk would have given them.
 *
 * If the result is kept, saving it — uploading the bytes and stamping
 * `aiGenerated: true` + `generationId` onto the new ProductMediaResources
 * entry — is the CALLER's responsibility. Nothing here does that; the
 * server never sees the bytes again after this response.
 */
/**
 * Turbo availability for a business: true / false when the server answered, null when it could not be asked
 * (network or server error), so the caller can try again later instead of caching a wrong "no".
 */
async function fetchTurboAvailability(businessId: string): Promise<boolean | null> {
  try {
    const response = await getRequest(
      `${ServiceEndpoints.AI_TURBO}/${encodeURIComponent(businessId)}`,
      true
    );
    if (response.status >= 500) return null;
    if (!response.ok) return false;
    const body = (await response.json().catch(() => null)) as TurboAvailabilityResponse | null;
    return body?.contents?.[0]?.available === true;
  } catch {
    return null;
  }
}

async function imageRevamp(
  request: ImageRevampRequest
): Promise<ImageRevampResponse> {
  const endpoint = ServiceEndpoints.AI_IMAGE_REVAMP;
  const includeBearerToken = true;
  const response = await postRequest(endpoint, request, includeBearerToken);

  if ([502, 503, 504].includes(response.status)) {
    throw new Error("The service is temporarily unavailable. Please try again in a moment.");
  }

  const data = await response.json().catch(() => null);

  if (!response.ok || !data || !data.contents?.length) {
    const errorInfo = data?.errorInformation?.[0];
    throw new Error(
      imageRevampErrorCopy(errorInfo?.errorCode, errorInfo?.errorDetail).message
    );
  }

  return data as ImageRevampResponse;
}

/**
 * AI poster generation for a business — see interfaces/PosterObject.ts for
 * the full contract. Same shape as imageRevamp immediately above (same
 * /api/v1/imagegen controller family): deliberately skips throwIfNotOk for
 * the same reason (it drops `errorCode`, which posterErrorCopy needs), and
 * gives 502/503/504 the same friendly bare-gateway copy.
 *
 * Unlike imageRevamp, this returns ONE finished poster (not a per-photo
 * edit) with `qrArea`/`reservedZone` pixel rects for the caller to overlay a
 * QR code onto client-side — see PosterContent in the interface file. Drawing
 * the QR and offering the download are NOT done here; this call only gets
 * the generated poster.
 */
async function generatePoster(request: PosterRequest): Promise<PosterResponse> {
  const endpoint = ServiceEndpoints.AI_IMAGE_POSTER;
  const includeBearerToken = true;
  const response = await postRequest(endpoint, request, includeBearerToken);

  if ([502, 503, 504].includes(response.status)) {
    throw new Error("The service is temporarily unavailable. Please try again in a moment.");
  }

  const data = await response.json().catch(() => null);

  if (!response.ok || !data || !data.contents?.length) {
    const errorInfo = data?.errorInformation?.[0];
    throw new Error(
      posterErrorCopy(errorInfo?.errorCode, errorInfo?.errorDetail).message
    );
  }

  return data as PosterResponse;
}


/**
 * AI visiting-card generation for a business — see
 * interfaces/VisitingCardObject.ts for the full contract. Same shape as
 * generatePoster immediately above (same /api/v1/imagegen controller
 * family): deliberately skips throwIfNotOk for the same reason (it drops
 * `errorCode`, which visitingCardErrorCopy needs), and gives 502/503/504 the
 * same friendly bare-gateway copy.
 */
async function generateVisitingCard(request: VisitingCardRequest): Promise<VisitingCardResponse> {
  const endpoint = ServiceEndpoints.AI_IMAGE_VISITING_CARD;
  const includeBearerToken = true;
  const response = await postRequest(endpoint, request, includeBearerToken);

  if ([502, 503, 504].includes(response.status)) {
    throw new Error("The service is temporarily unavailable. Please try again in a moment.");
  }

  const data = await response.json().catch(() => null);

  if (!response.ok || !data || !data.contents?.length) {
    const errorInfo = data?.errorInformation?.[0];
    throw new Error(
      visitingCardErrorCopy(errorInfo?.errorCode, errorInfo?.errorDetail).message
    );
  }

  return data as VisitingCardResponse;
}

/**
 * AI NFC card face generation — see interfaces/NfcCardObject.ts for the
 * contract. Same shape as generateVisitingCard above: skips throwIfNotOk so
 * `errorCode` survives for nfcCardErrorCopy, and gives 502/503/504 the same
 * friendly gateway copy.
 */
async function generateNfcCard(request: NfcCardRequest): Promise<NfcCardResponse> {
  const endpoint = ServiceEndpoints.AI_IMAGE_NFC_CARD;
  const includeBearerToken = true;
  const response = await postRequest(endpoint, request, includeBearerToken);

  if ([502, 503, 504].includes(response.status)) {
    throw new Error("The service is temporarily unavailable. Please try again in a moment.");
  }

  const data = await response.json().catch(() => null);

  if (!response.ok || !data || !data.contents?.length) {
    const errorInfo = data?.errorInformation?.[0];
    throw new Error(
      nfcCardErrorCopy(errorInfo?.errorCode, errorInfo?.errorDetail).message
    );
  }

  return data as NfcCardResponse;
}

export default {
  fetchAutofill,
  fetchMarketPrice,
  generateBulkProducts,
  bulkImageAnalyze,
  imageRevamp,
  fetchTurboAvailability,
  generatePoster,
  generateVisitingCard,
  generateNfcCard,
};
